import { Component, Output, EventEmitter, Signal, computed, inject, signal } from '@angular/core';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { AuthorizeService } from 'src/api-authorization/authorize.service';

interface NavItem {
    label: string;
    route: Signal<string>;
    icon: string;
    canView: Signal<boolean>;
}

@Component({
    selector: 'app-left-nav',
    templateUrl: './left-nav.component.html',
    styleUrls: ['./left-nav.component.scss'],
    imports: [RouterLinkActive, RouterLink]
})
export class LeftNavComponent {
    private authorizeService = inject(AuthorizeService);

    @Output() isLeftNavCollapsed: EventEmitter<boolean> = new EventEmitter();

    // Trading partners have a single partner; their Companies link targets their own id.
    // A multi-partner user who hasn't selected one has no id yet, so hide it until they do.
    private readonly isTradingPartner = computed(() =>
        this.authorizeService.isAdmin$()
        && !this.authorizeService.isSuperAdmin$()
        && this.authorizeService.tradingPartnerId$() !== null);

    private readonly canViewCarrierGuide = computed(() => this.authorizeService.isSuperAdmin$() || this.isTradingPartner());

    // Each item's visibility signal mirrors its route guard: isSuperAdmin$ = IchraX only
    // (SuperAdminGuard); isAdmin$ = IchraX or Trading Partner (AdminGuard).
    private readonly navItems: NavItem[] = [
        { label: 'Dashboard', route: signal('/admin/dashboard'), icon: 'fa-gauge-high', canView: this.authorizeService.isAdmin$ },
        { label: 'Trading Partners', route: signal('/admin/trading-partners'), icon: 'fa-handshake', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Submissions', route: signal('/admin/submissions'), icon: 'fa-inbox', canView: this.authorizeService.isAdmin$ },
        { label: 'Users', route: signal('/admin/users'), icon: 'fa-users', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Vendors', route: signal('/admin/vendors'), icon: 'fa-building-columns', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Carrier Guide', route: signal('/admin/carrier-guide'), icon: 'fa-book-open', canView: this.canViewCarrierGuide },
        { label: 'Snapshot Validation', route: signal('/admin/snapshot-validation'), icon: 'fa-shield-check', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Error Logs', route: signal('/admin/error-logs'), icon: 'fa-triangle-exclamation', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Companies', route: computed(() => `/admin/trading-partners/${this.authorizeService.tradingPartnerId$()}/companies`), icon: 'fa-briefcase', canView: this.isTradingPartner },
        { label: 'Reports', route: signal('/admin/reports'), icon: 'fa-chart-column', canView: this.authorizeService.isAdmin$ },
        { label: 'Test Suite', route: signal('/admin/test-suite'), icon: 'fa-clipboard-check', canView: this.authorizeService.isSuperAdmin$ },
        { label: 'Admin Guide', route: signal('/admin/admin-guide'), icon: 'fa-book', canView: this.authorizeService.isSuperAdmin$ }
    ];

    readonly visibleItems = computed(() => this.navItems.filter(item => item.canView()));
}
